import { Component, Input, OnInit, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Authorization, UserRole } from '@data/models/authorization.model';
import { Observable, Subject, Subscription } from 'rxjs';
import { Company } from '@data/models/company.model';
import { switchMap } from 'rxjs/operators';
import { CompanyService } from '@data/services/company.service';
import { ProjectService } from '@data/services/project.service';
import { EmbedProject, EmbedProperties, ProjectWithRoles } from '@data/models/project.model';
import { Role } from '@data/models/role.model';
import { cloneDeep, concat, isEmpty } from 'lodash';
import { AuthorizationService } from '@data/services/authorization.service';
import { Project } from '@data/models/project.model';

interface Tab {
    company: Company;
    projects: { [id: string]: ProjectWithRoles };
}

const PROJECT_LIMIT = 1000;

@Component({

    selector: 'syn-bot-authorizations-form',
    templateUrl: 'bot-authorizations-form.component.html',
    styleUrls: ['bot-authorizations-form.component.scss']
})
export class BotAuthorizationsFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isInternal: boolean;
    @Input() authorizationsForm: FormGroup;
    @Input() userId: string;
    @Input() roles: Role[];

    public loading = false;

    public pickerCompanies$: Observable<Company[]>;
    public companyPickerContentLoader = new Subject<{ term: string; strict: boolean }>();
    public company = new FormControl();
    public tabs: Tab[] = [];
    public initialProjects: { [id: string]: ProjectWithRoles } = {};
    public initialAuthorizations: Authorization[] = [];
    public selectedTab = new FormControl(0);

    public starProjectMode = false;
    public starProjectRoles = new FormControl([]);

    private companySubscription: Subscription;
    private userRoles: UserRole[];

    constructor(
        private readonly companyService: CompanyService,
        private readonly projectService: ProjectService,
        private readonly authorizationService: AuthorizationService,
    ) { }

    async ngOnInit(): Promise<void> {
        this.pickerCompanies$ = this.companyPickerContentLoader.pipe(
            switchMap(search => {
                const safeTerm = search.term.replace(/"/g, '');
                const filter = `name:${search.strict ? '' : '~'}"${safeTerm}" status:"ACTIVE"`;
                return this.companyService.findCompanies({ filter: filter, limit: 20 });
            })
        );

        this.companySubscription = this.company.valueChanges.subscribe(this.addCompany.bind(this));

        if (this.userId) {
            this.userRoles = await this.authorizationService.getRolesByUser(this.userId).toPromise();

            if (this.authorizationsForm.controls['userAuthorizations']) {
                const authorizations = this.getAuthorizations();
                const starRightIndex = authorizations.findIndex((authorization) => authorization.projectId === '*');

                if (starRightIndex !== -1) {
                    this.starProjectMode = true;
                    this.starProjectRoles.setValue(authorizations[starRightIndex].roles);
                    authorizations.splice(starRightIndex, 1);
                }

                if (authorizations && authorizations.length) {
                    await this.populateTabs(authorizations);
                }

                this.initialAuthorizations = authorizations;
            }
        } else {
            this.userRoles = [];
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.isInternal && changes.isInternal.currentValue !== changes.isInternal.previousValue && !this.isInternal) {
            this.starProjectMode = false;
        }
    }

    public ngOnDestroy(): void {
        this.companySubscription.unsubscribe();
    }

    public resetData(): void {
        this.tabs = [];
    }

    public getTouchedAuthorizations(): Authorization[] {
        if (this.starProjectMode) {
            return Object.values(this.initialProjects).map((project) => {
                return {
                    projectId: project.id,
                    roles: [],
                };
            }).concat([{
                projectId: '*',
                roles: this.starProjectRoles.value,
            }]);
        }

        const projects: { [id: string]: ProjectWithRoles } = this.tabs.reduce((acc, curr) => {
            return { ...acc, ...curr.projects };
        }, {});

        const removedProjects = Object.values(this.initialProjects).filter((project) => project.roles.length && !projects[project.id]);

        const authorizations = Object.values(projects).reduce((acc, curr) => {
            if (!this.initialProjects[curr.id] && curr.roles.length) {
                return acc.concat([{
                    projectId: curr.id,
                    roles: curr.roles,
                }]);
            }

            if (!this.initialProjects[curr.id] && !curr.roles.length) {
                return acc;
            }

            return acc.concat([{
                projectId: curr.id,
                roles: curr.roles,
            }]);
        }, removedProjects.map((project) => {
            return {
                projectId: project.id,
                roles: [],
            };
        }));

        if (this.isInternal) {
            authorizations.concat([{
                projectId: '*',
                roles: [],
            }]);
        }

        return authorizations;
    }

    public removeTab(index: number) {
        this.tabs.splice(index, 1);
    }

    public getAuthorizations(): Authorization[] {
        if (!this.authorizationsForm || !this.authorizationsForm.controls['userAuthorizations']) {
            return [];
        }

        return this.authorizationsForm.controls['userAuthorizations'].value;
    }

    private async populateTabs(authorizations: Authorization[]): Promise<void> {
        const companiesIds: string[] = authorizations.map(({ $project }: Authorization) => $project.customer);

        const [companies, projects] = await Promise.all([
            this.companyService.findCompanies({ filter: `id:(${companiesIds.join('|')})` }).toPromise(),
            this.projectService.findProjects({ filter: `customer:(${companiesIds.join('|')})`, limit: PROJECT_LIMIT }).toPromise()
        ]);

        await Promise.all(companies.map(async (company: Company) => {
            const companyProjects = projects.filter((project) => project.customer === company.id);

            this.tabs.push({
                company,
                projects: this.mergeProjectsWithRoles(companyProjects, this.userRoles),
            });
        }));

        this.initialProjects = this.tabs.reduce((acc, curr) => {
            return { ...acc, ...cloneDeep(curr.projects) };
        }, {});
    }

    private mergeProjectsWithRoles(projects: Project[], roles: UserRole[]): { [id: string]: ProjectWithRoles } {
        const formattedProjects: { [id: string]: ProjectWithRoles } = {};

        projects.map((project: Project) => {
            formattedProjects[project.id] = {
                ...project,
                roles: roles.find((role) => role.projectId === project.id)?.roles || [],
            };
        });

        return formattedProjects;
    }

    private async addCompany(company: Company): Promise<void> {
        if (!company) {
            return;
        }

        this.company.reset();

        const index = this.tabs.findIndex((tab) => tab.company.id === company.id);
        if (index > -1) {
            this.selectedTab.setValue(index);
            return;
        }

        this.loading = true;
        let data: EmbedProject<keyof EmbedProperties>[];
        let page = 1;
        let projects: Project[];
        do {
            data = await this.projectService.findProjects({ filter: `customer:${company.id}`, limit: 1000, page }).toPromise();
            projects = concat(projects, data);
            page++;
        } while (!isEmpty(data));
        projects = projects.filter(project => {
            return project !== undefined;
        });

        this.tabs.push({
            company,
            projects: this.mergeProjectsWithRoles(projects, this.userRoles),
        });
        this.loading = false;

        this.selectedTab.setValue(this.tabs.length - 1);
    }
}
